import i18next from 'i18next'
import { SideEnum } from 'types/api/enums'
import { FormState, ScoreType, Tiebreak, Set, ScoreValidationError, ScoreSetObj } from '../types'

interface MatchFormatConfig {
  match: MatchConfig
  set: ScoreConfig
  tiebreak: ScoreConfig
  superTiebreak?: ScoreConfig
}

export interface ScoreConfig {
  pointsToWin: number
  pointsMax: number
  minPointsDifference: number
}

export interface MatchConfig {
  sets: number
  tiebreaks: number
  superTiebreaks: number
}

type CreateReturn<T extends ScoreType> = T extends 'set' ? Set : Tiebreak<T>

export class ScoreManager {
  private _config: MatchFormatConfig
  private _validationErrors: ScoreValidationError[] = []

  constructor(config: MatchFormatConfig) {
    this._config = config
  }

  create<T extends ScoreType>(type: T, pointsToWin?: number, pointsMax?: number): CreateReturn<T> {
    return {
      type,
      pointsToWin: pointsToWin || this._config[type]?.pointsToWin || 1,
      pointsMax: pointsMax || this._config[type]?.pointsMax || 1,
      points: {
        [SideEnum.SIDE1]: null,
        [SideEnum.SIDE2]: null,
      },
      winner: null,
      isEditable: true,
      ...(type === 'set' ? { tiebreak: null } : {}),
    } as CreateReturn<T>
  }

  createInitialScore() {
    const sets: (Set | Tiebreak<'tiebreak'> | Tiebreak<'superTiebreak'>)[] = []

    for (let i = 0; i < this._config.match.sets; i++) {
      sets.push(this.create('set'))
    }

    if (!sets.length && this._config.match.tiebreaks) {
      for (let i = 0; i < this._config.match.tiebreaks; i++) {
        sets.push(this.create('tiebreak'))
      }
    }

    if (this._config.match.superTiebreaks) {
      sets.push(this.create('superTiebreak'))
    }

    return {
      sets,
      matchWinner: null,
      errors: [],
    }
  }

  clearErrors() {
    this._validationErrors = []
  }

  addError(setType: ScoreType, setIndex: number, errorMsg: string) {
    this._validationErrors.push({ setType, setIndex, message: errorMsg })
  }

  getErrors() {
    return this._validationErrors
  }

  getWinner(score: Set | Tiebreak<'tiebreak'> | Tiebreak<'superTiebreak'>) {
    if (score['tiebreak']) return score.winner === score['tiebreak'].winner ? score.winner : null
    return score.winner
  }

  isScoreComplete<T extends ScoreType>(score: Tiebreak<T>) {
    return score.points.SIDE1 !== null && score.points.SIDE2 !== null
  }

  throwIfScoreIncomplete<T extends ScoreType>(score: Tiebreak<T>) {
    if (!this.isScoreComplete(score)) throw new Error('Score not complete')
  }

  getCurrentPointsDifference<T extends ScoreType>(score: Tiebreak<T>) {
    this.throwIfScoreIncomplete(score)
    return (score.points.SIDE1 as number) - (score.points.SIDE2 as number)
  }

  checkMatchWinner(side1Points: number, side2Points: number) {
    const totalSets =
      (this._config.match.sets || this._config.match.tiebreaks) + this._config.match.superTiebreaks
    if (typeof totalSets !== 'number') return null
    const minSetsToWin = Math.floor(totalSets / 2) + 1
    return side1Points >= minSetsToWin
      ? SideEnum.SIDE1
      : side2Points >= minSetsToWin
      ? SideEnum.SIDE2
      : null
  }

  checkForErrors<T extends ScoreType>(score: ScoreSetObj<T>, setIndex: number) {
    if (score.type === 'set' && score.tiebreak) {
      if (score.tiebreak.winner && score.winner !== score.tiebreak.winner)
        this.addError(
          score.type,
          setIndex,
          i18next.t('postScore.scoreValidationError.invalidScore', { ns: 'strSocialMatches' })
        )

      if (!score.tiebreak.winner && this.isScoreComplete(score.tiebreak)) {
        this.addError(
          score.type,
          setIndex,
          i18next.t('postScore.scoreValidationError.invalidScore', { ns: 'strSocialMatches' })
        )
      }
    }

    if (!score.winner && this.isScoreComplete(score)) {
      this.addError(
        score.type,
        setIndex,
        i18next.t('postScore.scoreValidationError.invalidScore', { ns: 'strSocialMatches' })
      )
    }
  }

  hasPointsToWin<T extends ScoreType>(score: Tiebreak<T>, side: SideEnum) {
    this.throwIfScoreIncomplete(score)
    return (score.points[side] as number) >= score.pointsToWin
  }

  hasMoreThanMinPointsToWin<T extends ScoreType>(score: Tiebreak<T>, side: SideEnum) {
    this.throwIfScoreIncomplete(score)
    return (score.points[side] as number) > score.pointsToWin
  }

  hasMinPointsDifference<T extends ScoreType>(
    score: Tiebreak<T>,
    side: SideEnum,
    minPointsDifference?: number
  ) {
    this.throwIfScoreIncomplete(score)
    const minPointsDiff = minPointsDifference || this._config[score.type]?.minPointsDifference || 1
    const thisSide = side
    const otherSide = side === SideEnum.SIDE1 ? SideEnum.SIDE2 : SideEnum.SIDE1

    return (score.points[thisSide] as number) - (score.points[otherSide] as number) >= minPointsDiff
  }

  hasMoreThanMinPointsDifference<T extends ScoreType>(
    score: Tiebreak<T>,
    side: SideEnum,
    minPointsDifference?: number
  ) {
    this.throwIfScoreIncomplete(score)
    const minPointsDiff = minPointsDifference || this._config[score.type]?.minPointsDifference || 1
    const thisSide = side
    const otherSide = side === SideEnum.SIDE1 ? SideEnum.SIDE2 : SideEnum.SIDE1
    return (score.points[thisSide] as number) - (score.points[otherSide] as number) > minPointsDiff
  }

  hasSideWon<T extends ScoreType>(score: Tiebreak<T>, side: SideEnum): boolean {
    if (!this.hasPointsToWin(score, side) || !this.hasMinPointsDifference(score, side)) return false
    if (
      this.hasMoreThanMinPointsToWin(score, side) &&
      this.hasMoreThanMinPointsDifference(score, side)
    )
      return false
    return true
  }

  validateTiebreak<T extends Exclude<ScoreType, 'set'>>(score: ScoreSetObj<T>) {
    let updated: ScoreSetObj<T> | undefined

    if (!this.isScoreComplete(score)) {
      if (score.winner) {
        updated = { ...score, winner: null }
      }
    } else if (this.hasSideWon(score, SideEnum.SIDE1)) {
      if (score.winner !== SideEnum.SIDE1) updated = { ...score, winner: SideEnum.SIDE1 }
    } else if (this.hasSideWon(score, SideEnum.SIDE2)) {
      if (score.winner !== SideEnum.SIDE2) updated = { ...score, winner: SideEnum.SIDE2 }
    } else if (
      this.hasPointsToWin(score, SideEnum.SIDE1) &&
      this.hasPointsToWin(score, SideEnum.SIDE2)
    ) {
      if (score.winner) updated = { ...score, winner: null }
    } else if (
      !this.hasPointsToWin(score, SideEnum.SIDE1) &&
      !this.hasPointsToWin(score, SideEnum.SIDE2)
    ) {
      if (score.winner) updated = { ...score, winner: null }
    } else {
      if (score.winner) {
        updated = { ...score, winner: null }
      }
    }

    return updated
  }

  validateSet<T extends 'set'>(score: ScoreSetObj<T>) {
    let updated: ScoreSetObj<T> | undefined

    if (!this.isScoreComplete(score)) {
      if (score.winner) {
        updated = { ...score, winner: null }
      } else if (score.tiebreak) {
        updated = { ...score, tiebreak: null }
      }
    } else if (this.hasSideWon(score, SideEnum.SIDE1)) {
      if (score.winner !== SideEnum.SIDE1 || (score.winner === SideEnum.SIDE1 && score.tiebreak))
        updated = { ...score, winner: SideEnum.SIDE1, tiebreak: null }
    } else if (this.hasSideWon(score, SideEnum.SIDE2)) {
      if (score.winner !== SideEnum.SIDE2 || (score.winner === SideEnum.SIDE2 && score.tiebreak))
        updated = { ...score, winner: SideEnum.SIDE2, tiebreak: null }
    } else if (
      this.hasPointsToWin(score, SideEnum.SIDE1) &&
      this.hasPointsToWin(score, SideEnum.SIDE2)
    ) {
      if (this.getCurrentPointsDifference(score)) {
        if (!score.tiebreak) {
          updated = {
            ...score,
            winner: this.getCurrentPointsDifference(score) > 0 ? SideEnum.SIDE1 : SideEnum.SIDE2,
            tiebreak: this.create('tiebreak'),
          }
        } else {
          const updatedTB = this.validateTiebreak(score.tiebreak)
          if (updatedTB)
            updated = {
              ...score,
              tiebreak: updatedTB,
            }
        }
      } else {
        if (score.winner) {
          updated = { ...score, winner: null }
        } else if (score.tiebreak) {
          updated = { ...score, tiebreak: null }
        }
      }
    } else if (
      !this.hasPointsToWin(score, SideEnum.SIDE1) &&
      !this.hasPointsToWin(score, SideEnum.SIDE2)
    ) {
      if (score.winner) updated = { ...score, winner: null }
    } else {
      if (score.winner) {
        updated = { ...score, winner: null }
      } else if (score.tiebreak) {
        updated = { ...score, tiebreak: null }
      }
    }

    return updated
  }

  validateAndUpdate(matchScore: FormState['score']): FormState['score'] | undefined {
    let updatedMatchScore
    let setWon: Record<SideEnum, number> = {
      [SideEnum.SIDE1]: 0,
      [SideEnum.SIDE2]: 0,
    }
    let matchWinner: Set['winner'] = null
    let isScoreValid = true

    this.clearErrors()

    matchScore?.sets.forEach((set, i: number) => {
      if (matchWinner) {
        if (set.points.SIDE1 !== null || set.points.SIDE2 !== null) {
          updatedMatchScore = { ...(updatedMatchScore || matchScore) }
          updatedMatchScore.sets[i] = {
            ...this.create(updatedMatchScore.sets[i].type),
            isEditable: false,
          }
        }
      } else {
        const validate =
          set.type === 'set' ? this.validateSet.bind(this) : this.validateTiebreak.bind(this)
        //@ts-ignore
        const updatedSet = validate(set)

        if (updatedSet) {
          updatedMatchScore = { ...(updatedMatchScore || matchScore) }
          updatedMatchScore.sets[i] = updatedSet
        }

        const setWinner = this.getWinner(updatedSet || set)

        if (isScoreValid) {
          if (!set.isEditable) {
            updatedMatchScore = { ...(updatedMatchScore || matchScore) }
            updatedMatchScore.sets[i].isEditable = true
          }

          if (setWinner) {
            setWon[setWinner]++
          } else {
            isScoreValid = false
          }

          matchWinner = this.checkMatchWinner(setWon[SideEnum.SIDE1], setWon[SideEnum.SIDE2])
          if (matchWinner) isScoreValid = false
        } else if (set.isEditable) {
          updatedMatchScore = { ...(updatedMatchScore || matchScore) }
          updatedMatchScore.sets[i].isEditable = false
        }

        let currentScore = updatedMatchScore || matchScore
        if (currentScore.sets[i].isEditable) this.checkForErrors(currentScore.sets[i], i)
      }
    })

    if (matchWinner) {
      if (!matchScore?.matchWinner || matchScore.matchWinner !== matchWinner) {
        updatedMatchScore = { ...(updatedMatchScore || matchScore) }
        updatedMatchScore.matchWinner = matchWinner
        updatedMatchScore.errors = []
      }
    } else if (matchScore?.matchWinner) {
      updatedMatchScore = { ...(updatedMatchScore || matchScore) }
      updatedMatchScore.matchWinner = null
    }

    return updatedMatchScore
  }
}
